import React, { useState, useEffect, useRef } from 'react';
import GlobalFunction from './globalFunction';
import { Grid, Row, Col, FormGroup, Button, FormControl, Glyphicon, DropdownButton, MenuItem, ListGroup, ListGroupItem, Panel, Modal } from 'react-bootstrap';
import EventSystem from './EventSystem';
import _ from 'lodash';
import keyGen from 'random-key';
import Switch from 'react-switch';
import Channel from './channel';
import mediaDeviceClass from './mediaDeviceClass';
import webRtcController from './webRtcController';
import { prefetchTurnCredentials, getTurnCredentials } from './turnCredentials';
import { Tile, Title, Body, LoggingIndicator } from './style/tile.style';
import './css/slider.css';
import './css/webcamModule.css';
import 'rc-slider/assets/index.css';

function makeInputChannel() {
    return {
        stream: null, rawStream: null, callStatus: 'none', registeredName: '',
        remoteAudioMuted: false, remoteStreamReceived: false,
        remoteConnectionSuccessful: false, initiatedHangup: false,
        bytesReceived: 0, bytesSent: 0, incomingBitrate: 0,
        outgoingBitrate: 0, jitter: [], reportObject: null,
        handRaised: false, handRaisedAt: 0
    };
}

const NUM_CHANNELS = 5;

export default function AudioHub({ parameters, onSendMessage, addLogEntry, enableModuleLogging, mode, demoMode }) {
    const initChannels = () => Array.from({ length: NUM_CHANNELS }, makeInputChannel);

    const [moduleWidth, setModuleWidth] = useState(0);
    const [moduleHeight, setModuleHeight] = useState(0);
    const [validationError, setValidationError] = useState(null);
    const [myRegisteredName, setMyRegisteredName] = useState('');
    const [registeredNames, setRegisteredNames] = useState([]);
    const [callStatus, setCallStatus] = useState('none');
    const [cameraMuted, setCameraMuted] = useState(false);
    const [micMuted, setMicMuted] = useState(false);
    const [componentMounted, setComponentMounted] = useState(false);
    const [fileManager, setFileManager] = useState(false);
    const [gracefulCallTermination, setGracefulCallTermination] = useState(false);
    const [initiatedHangUp, setInitiatedHangUp] = useState(false);
    const [terminationMessage, setTerminationMessage] = useState('');
    const [browserSupportFails, setBrowserSupportFails] = useState(false);
    const [sendingLocalVideo, setSendingLocalVideo] = useState(true);
    const [sendingLocalAudio, setSendingLocalAudio] = useState(true);
    const [gettingLocalStream, setGettingLocalStream] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [inputChannels, setInputChannels] = useState(initChannels);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [channelHeight, setChannelHeight] = useState(0);
    const [myMediaDeviceClass, setMyMediaDeviceClass] = useState(null);
    const [currentSliderValue, setCurrentSliderValue] = useState(0);
    const [audioHubName, setAudioHubName] = useState('');
    const [recording, setRecording] = useState(false);
    const [recordingSupported, setRecordingSupported] = useState(false);
    const [hubSwitchDisabled, setHubSwitchDisabled] = useState(false);
    const [hubRegistered, setHubRegistered] = useState(false);
    const [outputMasterGainVolume, setOutputMasterGainVolume] = useState(33);
    const [deskInputStreamPreMix, setDeskInputStreamPreMix] = useState(null);
    const [localVideoStream, setLocalVideoStream] = useState(null);
    const [parentCallStatus, setParentCallStatus] = useState(null);
    const [panelBodyContainerHeight, setPanelBodyContainerHeight] = useState(0);
    const [raisedHands, setRaisedHands] = useState([]);

    // Mutable instance vars
    const localStreamRef = useRef({});
    const audioCTXRef = useRef({});
    const deskInputNodeRef = useRef(undefined);
    const webRtcControllerRef = useRef(Array(NUM_CHANNELS).fill(null));
    const mediaTrackForPcRef = useRef(Array(NUM_CHANNELS).fill(undefined));
    const outputStreamRef = useRef(Array(NUM_CHANNELS).fill(null));
    const localAudioRefs = useRef([]);
    const recordingStreamNodeRef = useRef(undefined);
    const mediaRecorderRef = useRef(undefined);
    const chunksRef = useRef([]);
    const audioURLRef = useRef(undefined);
    const recordingStartedAtRef = useRef(null);
    const masterOutputGainRef = useRef(undefined);
    const keepAlivesRef = useRef(Array.from({ length: NUM_CHANNELS }, () => ({ lostCount: 0, timerID: 0 })));
    const timerIDRef = useRef(undefined);
    const intervalIDRef = useRef(undefined);
    const previousTitleRef = useRef('');

    const previewStreamRef = useRef(null);

    // DOM refs
    const container = useRef(null);
    const previewVideoRef = useRef(null);
    const nonVideoRows = useRef(null);
    const panelBodyContainer = useRef(null);
    const panelHeading = useRef(null);
    const firstCol = useRef(null);
    const mp3 = useRef(null);
    const localAudioX = useRef(null);
    const localAudio0 = useRef(null);
    const localAudio1 = useRef(null);
    const localAudio2 = useRef(null);
    const localAudio3 = useRef(null);
    const localAudio4 = useRef(null);
    const localAudio5 = useRef(null);

    // Live refs for props/state used in callbacks
    const liveRef = useRef({ onSendMessage, parameters, audioHubName: '' });
    liveRef.current.onSendMessage = onSendMessage;
    liveRef.current.parameters = parameters;
    liveRef.current.audioHubName = audioHubName;
    liveRef.current.myRegisteredName = myRegisteredName;

    // State ref for use in EventSystem/WebRTC callbacks
    const sr = useRef({});
    sr.current = {
        callStatus, myRegisteredName, registeredNames, inputChannels,
        hubRegistered, gracefulCallTermination, initiatedHangUp,
        cameraMuted, micMuted, sendingLocalVideo, sendingLocalAudio,
        parentCallStatus, containerWidth, containerHeight, audioHubName,
        myMediaDeviceClass, raisedHands,
    };

    const inputChannelsRef = useRef(inputChannels);
    inputChannelsRef.current = inputChannels;

    useEffect(() => {
        localAudioRefs.current = [
            localAudio0.current, localAudio1.current, localAudio2.current,
            localAudio3.current, localAudio4.current, localAudio5.current,
        ];
        for (let x = 0; x < localAudioRefs.current.length; x++) {
            localAudioRefs.current[x].volume = 1;
        }

        if (mode === 'setup') return;

        prefetchTurnCredentials();

        const token = keyGen.generate;
        const pcs = { action: 'webcamCallStatusUpdate', instanceToken: token, callStatus: 'none' };
        setParentCallStatus(pcs);

        getModuleSize(
            parameters.containerHeight.container,
            parameters.config.w,
            parameters.containerHeight.maxCols
        );

        EventSystem.subscribe('AudioClient', processMessageStable.current);
        EventSystem.subscribe('AudioHub', processMessageStable.current);

        const mdParams = {
            module_name: parameters.config.module_name,
            event_screen_module_id: parameters.config.event_screen_module_id,
            callType: parameters.config.other_config.callType,
            audioMandatory: true
        };
        const mdc = new mediaDeviceClass(mdParams);
        setMyMediaDeviceClass(mdc);
        liveRef.current.mdc = mdc;

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'register';
        liveRef.current.onSendMessage(msgObj);

        const calcWidth = Math.round((window.innerWidth - 35) / parameters.containerHeight.maxCols) * parameters.config.w;
        const pbHeight = panelBodyContainer.current.clientHeight;
        const nvHeight = nonVideoRows.current.clientHeight;

        setContainerWidth(calcWidth);
        setContainerHeight(container.current.clientHeight);
        setPanelBodyContainerHeight(pbHeight);
        setChannelHeight((pbHeight - nvHeight - 25) / 2);

        const constraints = parameters.config.other_config.callType === 'video'
            ? { audio: true, video: true }
            : { audio: true, video: false };

        try {
            navigator.mediaDevices.getUserMedia(constraints)
                .then(stream => {
                    localStreamRef.current = stream;
                    getMediaDevice();
                    stopLocalDevice();
                })
                .catch(e => { alert('getUserMedia() error: ' + e.name); });

            navigator.mediaDevices.ondevicechange = () => { getMediaDevice(); };
        } catch (err) {
            setBrowserSupportFails(true);
        }

        // restore hub name and auto-register
        const locallyStoredData = JSON.parse(localStorage.getItem(
            parameters.config.module_name + '_' + parameters.config.event_screen_module_id
        ));
        if (locallyStoredData?.audioHubName) {
            setAudioHubName(locallyStoredData.audioHubName);
        }

        setTimeout(() => { hubRegisterChanged(); }, 5000);

        return () => {
            EventSystem.unsubscribe('AudioClient', processMessageStable.current);
            EventSystem.unsubscribe('AudioHub', processMessageStable.current);
            const chs = inputChannelsRef.current;
            for (let i = 0; i < chs.length; i++) {
                if (chs[i].callStatus === 'active') {
                    const msg = getMessageObjectProForma();
                    msg.action = 'bye';
                    msg.callReceiver = sr.current.myRegisteredName;
                    msg.callInitiator = chs[i].registeredName;
                    liveRef.current.onSendMessage(msg);
                }
            }
            if (intervalIDRef.current !== undefined) clearInterval(intervalIDRef.current);
        };
    }, []);

    // CWRP: container size change
    const firstSizeRender = useRef(true);
    useEffect(() => {
        if (firstSizeRender.current) { firstSizeRender.current = false; return; }
        getModuleSize(
            parameters.containerHeight.container,
            parameters.config.w,
            parameters.containerHeight.maxCols
        );
    }, [parameters.containerHeight.container, parameters.config.w]);

    const processMessageStable = useRef(function(msg) { processMessageImpl(msg); });

    useEffect(() => {
        if (raisedHands.length === 0) {
            if (previousTitleRef.current.length > 0) {
                document.title = previousTitleRef.current;
                previousTitleRef.current = '';
            }
            return;
        }
        if (previousTitleRef.current.length === 0) {
            previousTitleRef.current = document.title;
        }
        document.title = 'HAND RAISED - ' + raisedHands[0].registeredName;
    }, [raisedHands]);

    function processMessageImpl(messageObject) {
        if (liveRef.current.parameters.config.event_grouping_seq !== messageObject.event_grouping_seq) return;

        const s = sr.current;

        if (messageObject.action === 'debugResponse') {
            showDebugMessage(messageObject);
        } else if (messageObject.action === 'requestConnectedTone') {
            sendConnectedTone(messageObject.callInitiator);
        } else if (messageObject.action === 'register') {
            nameRegisterStart();
        } else if (messageObject.action === 'callingRequest' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            handleInboundCall(messageObject);
        } else if (messageObject.action === 'instanceRegisteredName') {
            const time = new Date();
            const debugObject = {
                uniqueKey: 'X/' + time.getTime(),
                timestamp: time.getTime(), channelID: 'X', sequence: 0,
                sourceMethod: messageObject.audioinput,
                connectionState: messageObject.registeredName
            };
            EventSystem.publish('webRTCdebug', { registeredName: s.myRegisteredName, debugObject });
            const newMsg = getMessageObjectProForma();
            newMsg.sourceModuleName = 'remoteWebRTCdebug';
            newMsg.callReceiver = s.myRegisteredName;
            newMsg.client = 'AudioHub';
            newMsg.debugObject = debugObject;
            liveRef.current.onSendMessage(newMsg);

            const hubFullName = liveRef.current.parameters.config.description + ' - ' + liveRef.current.audioHubName;
            if (messageObject.registeredName !== hubFullName) {
                setRegisteredNames(prev => {
                    const ns = {
                        registeredName: messageObject.registeredName,
                        containerHeight: messageObject.containerHeight,
                        containerWidth: messageObject.containerWidth,
                        sourceModuleName: messageObject.sourceModuleName,
                        callStatus: messageObject.callStatus
                    };
                    const existingIndex = prev.findIndex(r => r.registeredName === messageObject.registeredName);
                    const updated = [...prev];
                    if (existingIndex === -1) {
                        updated.push(ns);
                    } else {
                        updated[existingIndex] = { ...updated[existingIndex], ...ns };
                    }
                    if (updated.findIndex(r => r.registeredName === hubFullName) > -1) {
                        setValidationError('error');
                    }
                    return updated;
                });
            }
        } else if (messageObject.action === 'bye') {
            if (!s.hubRegistered) return;
            if (messageObject.callReceiver !== s.myRegisteredName) return;
            setGracefulCallTermination(true);
            const channel = checkInputChannel(messageObject.callInitiator);
            if (channel === 99) return;
            const chs = [...inputChannelsRef.current];
            const hangupReason = chs[channel].initiatedHangup ? '' : messageObject.callInitiator + ' hung up the call';
            updateRaisedHand(messageObject.callInitiator, false);
            chs[channel] = { ...chs[channel], callStatus: 'bye' };
            setInputChannels(chs);
            stopTheCall(channel, hangupReason);
        } else if (messageObject.action === 'audioMute' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            const channel = getInputChannel(messageObject.callInitiator);
            setInputChannels(prev => {
                const chs = [...prev];
                chs[channel] = { ...chs[channel], remoteAudioMuted: messageObject.micMuted };
                return chs;
            });
        } else if (messageObject.action === 'raiseHand' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            updateRaisedHand(messageObject.callInitiator, messageObject.handRaised);
        } else if (messageObject.action === 'remoteConnectionSuccess') {
            if (messageObject.callReceiver === s.myRegisteredName) {
                const channel = checkInputChannel(messageObject.callInitiator);
                if (channel !== 99) {
                    setInputChannels(prev => {
                        const chs = [...prev];
                        chs[channel] = { ...chs[channel], remoteConnectionSuccessful: true };
                        if (chs[channel].remoteStreamReceived && chs[channel].callStatus !== 'active') {
                            connectionSuccessful(messageObject.callInitiator);
                        }
                        return chs;
                    });
                }
            }
        } else if (messageObject.action === 'webrtc') {
            if (messageObject.callReceiver !== s.myRegisteredName &&
                messageObject.callInitiator !== s.myRegisteredName) return;
            const channel = checkInputChannel(messageObject.callInitiator);
            if (channel === 99) return;
            if (inputChannelsRef.current[channel].callStatus === 'bye') return;
            webRtcControllerRef.current[channel].handshakeReceived(messageObject);
        } else if (messageObject.action === 'keepAlive') {
            const channel = checkInputChannel(messageObject.callInitiator);
            if (messageObject.sourceModuleName === 'AudioClient') {
                clearTimeout(keepAlivesRef.current[channel].timerID);
                waitForKeepAliveReceipt(channel);
            }
        }
    }

    function updateRaisedHand(registeredName, handRaised) {
        if (!registeredName || registeredName.length === 0) return;
        const raisedAt = Date.now();

        setRaisedHands(prev => {
            const existing = prev.find(h => h.registeredName === registeredName);
            if (handRaised) {
                if (existing) return prev;
                return [...prev, { registeredName, raisedAt }];
            }
            return prev.filter(h => h.registeredName !== registeredName);
        });

        setInputChannels(prev => {
            const chs = [...prev];
            const channel = chs.findIndex(ch => ch.registeredName === registeredName);
            if (channel !== -1) {
                chs[channel] = {
                    ...chs[channel],
                    handRaised,
                    handRaisedAt: handRaised ? (chs[channel].handRaisedAt || raisedAt) : 0
                };
            }
            return chs;
        });
    }

    function lowerRaisedHand(registeredName) {
        updateRaisedHand(registeredName, false);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'raiseHand';
        msgObj.callInitiator = sr.current.myRegisteredName;
        msgObj.callReceiver = registeredName;
        msgObj.handRaised = false;
        liveRef.current.onSendMessage(msgObj);
    }

    function getMessageObjectProForma() {
        const p = liveRef.current.parameters;
        return {
            sourceModuleName: p.config.module_name,
            event_grouping_seq: p.config.event_grouping_seq,
            customerID: p.userProfile.customer_id,
            siteID: p.userProfile.site_id,
            eventID: p.config.event_id,
        };
    }

    function getModuleSize(height, columns, maxCols) {
        const width = Math.floor(GlobalFunction.getModuleWidth(columns, maxCols));
        setModuleWidth(width);
        setModuleHeight(height);
    }

    function getMediaDevice() {
        navigator.mediaDevices.enumerateDevices()
            .then(gotDevices)
            .catch(e => { alert('Error getting media devices'); });
    }

    function gotDevices(deviceInfo) {
        const mdc = sr.current.myMediaDeviceClass || liveRef.current.mdc;
        if (!mdc) return;
        mdc.updateDevices(deviceInfo);
        if (mdc.getSelectedDevices.videoinput === '') setSendingLocalVideo(false);
        if (mdc.getSelectedDevices.audioinput === '') setSendingLocalAudio(false);
        setMyMediaDeviceClass(mdc);
        setComponentMounted(true);
        if (mdc.needsSetup()) setShowModal(true);
        else if (mdc.getSelectedDevices.videoinput !== '') startPreview(mdc);
    }

    function getLocalStream() {
        const callType = liveRef.current.parameters.config.other_config.callType;
        const localDevice = callType === 'video' ? null : localAudio0.current;
        const mdc = sr.current.myMediaDeviceClass;
        if (!mdc) return;

        const audioConstraint = mdc.getDeviceID('audioinput');
        const rawVideoConstraint = mdc.getDeviceID('videoinput');
        const audioOutputId = mdc.getDeviceID('audiooutput');

        if (audioOutputId && localDevice) {
            localDevice.setSinkId(audioOutputId).catch(e => alert('Error attaching to SinkId: ' + e));
        }

        if (!audioConstraint && !rawVideoConstraint) {
            setGettingLocalStream(false);
            return;
        }

        // getDeviceID returns audio-style constraints for all device types.
        // For video: strip audio-only properties; fall back to true if deviceId is empty.
        let videoConstraint = false;
        if (rawVideoConstraint) {
            const vid = rawVideoConstraint.deviceId?.exact;
            videoConstraint = vid ? { deviceId: { exact: vid } } : true;
        }

        const constraints = { audio: audioConstraint, video: videoConstraint };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                localStreamRef.current = stream;
                setGettingLocalStream(true);
                const deskInputStreamPreMixNode = audioCTXRef.current.createMediaStreamSource(stream);
                setDeskInputStreamPreMix(deskInputStreamPreMixNode);
                setLocalVideoStream(stream.getVideoTracks().length > 0 ? stream : null);
                setHubSwitchDisabled(false);
            })
            .catch(e => {
                alert('getUserMedia() error gettingLocalStream: ' + e.name);
                setGettingLocalStream(false);
            });
    }

    function stopLocalDevice() {
        const s = sr.current;
        if (s.sendingLocalVideo || s.sendingLocalAudio) {
            const tracks = localStreamRef.current?.getTracks?.();
            if (tracks) tracks.forEach(t => t.stop());
        }
        localStreamRef.current = null;
        const callType = liveRef.current.parameters.config.other_config.callType;
        const localDevice = callType === 'video' ? null : localAudioX.current;
        if (localDevice) localDevice.srcObject = null;
    }

    function nameRegisterStart() {
        setRegisteredNames([]);
        const s = sr.current;
        if (s.myRegisteredName.length > 0) {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'instanceRegisteredName';
            msgObj.audioinput = s.myMediaDeviceClass?.getSelectedDevices.audioinput;
            msgObj.registeredName = s.myRegisteredName;
            msgObj.containerHeight = container.current.clientHeight;
            msgObj.containerWidth = container.current.clientWidth;
            msgObj.callStatus = 'none';
            liveRef.current.onSendMessage(msgObj);
        }
    }

    async function handleInboundCall(messageObject) {
        messageObject.action = 'callingResponse';
        const channelID = getInputChannel(messageObject.callInitiator);
        if (channelID === 99 || !deskInputNodeRef.current) {
            messageObject.response = 'busy';
        } else {
            const newMsgObj = getMessageObjectProForma();
            newMsgObj.callInitiator = messageObject.callInitiator;
            newMsgObj.callReceiver = sr.current.myRegisteredName;

            mediaTrackForPcRef.current[channelID] = audioCTXRef.current.createMediaStreamDestination();
            deskInputNodeRef.current.connect(mediaTrackForPcRef.current[channelID]);

            // Add hub's video tracks (if any) to the audio destination stream sent to caller
            const outStream = mediaTrackForPcRef.current[channelID].stream;
            if (localStreamRef.current) {
                localStreamRef.current.getVideoTracks().forEach(t => outStream.addTrack(t));
            }

            const pcConfig = await getTurnCredentials();
            const controllerParams = {
                messageObject: newMsgObj,
                channelID,
                myRegisteredName: sr.current.myRegisteredName,
                callType: 'video',
                localStream: outStream,
                sendingLocalVideo: localStreamRef.current?.getVideoTracks().length > 0,
                sendingLocalAudio: sr.current.sendingLocalAudio,
                pcConfig,
            };
            webRtcControllerRef.current[channelID] = new webRtcController(controllerParams);
            webRtcControllerRef.current[channelID].on('sendMessage', sendMessage);
            webRtcControllerRef.current[channelID].on('connectionSuccessful', connectionSuccessful);
            webRtcControllerRef.current[channelID].on('callFailed', callFailed);
            webRtcControllerRef.current[channelID].on('replaceTracks', callFailed);
            webRtcControllerRef.current[channelID].on('remoteStreamObtained', addStreamToDesk);
            webRtcControllerRef.current[channelID].on('stats', gatherStats);
            webRtcControllerRef.current[channelID].on('sendStateDebugInfo', processWebRTCDebugEvent);

            if (liveRef.current.parameters.config.other_config.audio_codec?.length > 0) {
                webRtcControllerRef.current[channelID].audio_codec = liveRef.current.parameters.config.other_config.audio_codec;
            }

            setInputChannels(prev => {
                const chs = [...prev];
                chs[channelID] = { ...chs[channelID], callStatus: 'beingCalled' };
                return chs;
            });

            messageObject.response = 'ok';
            const pcs = { ...sr.current.parentCallStatus, callStatus: 'beingCalled' };
            setParentCallStatus(pcs);
            liveRef.current.onSendMessage(pcs);
        }
        liveRef.current.onSendMessage(messageObject);
    }

    function sendMessage(messageObject) {
        messageObject.containerHeight = sr.current.containerHeight;
        messageObject.containerWidth = sr.current.containerWidth;
        liveRef.current.onSendMessage(messageObject);
    }

    function waitForKeepAliveReceipt(channel) {
        keepAlivesRef.current[channel].timerID = setTimeout(() => {
            console.log('=====> Missed first keepalive <=====');
            keepAlivesRef.current[channel].timerID = setTimeout(() => {
                console.log('=====> Missed second keepalive <=====');
            }, 1000);
        }, 1000);
    }

    function checkInputChannel(caller) {
        const chs = inputChannelsRef.current;
        for (let x = 0; x < chs.length; x++) {
            if (chs[x].registeredName === caller) return x;
        }
        return 99;
    }

    function getInputChannel(caller) {
        let channel = 99;
        const chs = [...inputChannelsRef.current];
        for (let x = 0; x < chs.length; x++) {
            if (chs[x].registeredName === caller) { channel = x; break; }
        }
        if (channel === 99) {
            for (let x = 0; x < chs.length; x++) {
                if (chs[x].registeredName.length === 0) {
                    chs[x] = { ...chs[x], registeredName: caller, callStatus: 'unknown' };
                    channel = x;
                    break;
                }
            }
            inputChannelsRef.current = chs;
            setInputChannels(chs);
        }
        return channel;
    }

    function callFailed(channel, e) {
        console.log('Call failed: ', e);
        stopTheCall(channel, 'Connection broken with ' + inputChannelsRef.current[channel].registeredName);
    }

    function connectionSuccessful(caller) {
        const channel = getInputChannel(caller);
        const chs = inputChannelsRef.current;
        if (chs[channel].callStatus === 'active' ||
            !chs[channel].remoteStreamReceived ||
            !chs[channel].remoteConnectionSuccessful) return;

        setInputChannels(prev => {
            const updated = [...prev];
            updated[channel] = { ...updated[channel], callStatus: 'active' };
            return updated;
        });
        setCallStatus('active');
        setGracefulCallTermination(false);

        const pcs = { ...sr.current.parentCallStatus, callStatus: 'active' };
        setParentCallStatus(pcs);
        liveRef.current.onSendMessage(pcs);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'register';
        liveRef.current.onSendMessage(msgObj);

        connectToOtherCalls(caller);
    }

    function connectToOtherCalls(caller) {
        const myChannelID = getInputChannel(caller);
        const chs = inputChannelsRef.current;
        for (let i = 0; i < chs.length; i++) {
            if (i !== myChannelID && chs[i].callStatus === 'active') {
                outputStreamRef.current[myChannelID].connect(mediaTrackForPcRef.current[i]);
                outputStreamRef.current[i].connect(mediaTrackForPcRef.current[myChannelID]);
            }
        }
    }

    function stopTheCall(callChannel, hangupReason) {
        webRtcControllerRef.current[callChannel]?.close();
        webRtcControllerRef.current[callChannel] = null;
        mediaTrackForPcRef.current[callChannel] = null;
        try { outputStreamRef.current[callChannel].disconnect(); } catch (err) { console.log('nothing to disconnect', err); }

        setInputChannels(prev => {
            const chs = [...prev];
            const registeredName = chs[callChannel].registeredName;
            if (registeredName.length > 0) {
                setRaisedHands(hands => hands.filter(h => h.registeredName !== registeredName));
            }
            chs[callChannel] = {
                ...chs[callChannel],
                callStatus: 'none', registeredName: '', remoteAudioMuted: false,
                bytesReceived: 0, bytesSent: 0, incomingBitrate: 0, outgoingBitrate: 0,
                jitter: [], stream: null, rawStream: null, remoteStreamReceived: false,
                initiatedHangup: false, remoteConnectionSuccessful: false,
                handRaised: false, handRaisedAt: 0
            };
            return chs;
        });

        const pcs = { ...sr.current.parentCallStatus, callStatus: 'none' };
        setParentCallStatus(pcs);
        liveRef.current.onSendMessage(pcs);

        if (intervalIDRef.current !== undefined) clearInterval(intervalIDRef.current);

        setIsActive(false);
        setGracefulCallTermination(false);
        setInitiatedHangUp(false);

        if (!sr.current.initiatedHangUp) {
            setTerminationMessage(hangupReason);
            timerIDRef.current = setTimeout(() => setTerminationMessage(''), 4000);
        }
    }

    // isActive not used in render currently but kept for symmetry
    const [isActive, setIsActive] = useState(false);

    function hangupCall(channel, caller) {
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'bye';
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.callInitiator = caller;
        liveRef.current.onSendMessage(msgObj);
        updateRaisedHand(caller, false);
        setInputChannels(prev => {
            const chs = [...prev];
            chs[channel] = { ...chs[channel], initiatedHangup: true };
            return chs;
        });
    }

    function sendKeepalive(channel, caller, count) {
        if (count === 0) waitForKeepAliveReceipt(channel);
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'keepAlive';
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.callInitiator = caller;
        liveRef.current.onSendMessage(msgObj);
    }

    function hubRegisterChanged() {
        const s = sr.current;
        const hubFullName = liveRef.current.parameters.config.description + ' - ' + liveRef.current.audioHubName;
        const isDup = s.registeredNames.findIndex(r => r.registeredName === hubFullName) !== -1;
        if (isDup) { setValidationError('error'); return; }
        setValidationError(null);

        const newHubRegistered = !s.hubRegistered;

        if (newHubRegistered) {
            setHubSwitchDisabled(true);
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            audioCTXRef.current = new AudioContext();
            window.audioHubCTX = audioCTXRef.current;
            recordingStreamNodeRef.current = audioCTXRef.current.createMediaStreamDestination();

            masterOutputGainRef.current = audioCTXRef.current.createGain();
            masterOutputGainRef.current.connect(audioCTXRef.current.destination);

            setRecordingSupported(setupMixedAudioRecorder());

            EventSystem.publish('nameRegistered', hubFullName);
            setMyRegisteredName(hubFullName);
            setSendingLocalVideo(s.myMediaDeviceClass?.getSelectedDevices.videoinput !== '');
            setSendingLocalAudio(s.myMediaDeviceClass?.getSelectedDevices.audioinput !== '');

            const key = liveRef.current.parameters.config.module_name + '_' + liveRef.current.parameters.config.event_screen_module_id;
            let locallyStoredData = JSON.parse(localStorage.getItem(key)) ?? {};
            locallyStoredData.audioHubName = liveRef.current.audioHubName;
            localStorage.setItem(key, JSON.stringify(locallyStoredData));

            setGettingLocalStream(true);
            stopPreview();
            getLocalStream();
        } else {
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setRecordingSupported(false);
            setMyRegisteredName('');
            deskInputNodeRef.current?.disconnect();
            audioCTXRef.current?.close();
            stopLocalDevice();
            setDeskInputStreamPreMix(null);
            setHubRegistered(false);
        }

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'register';
        liveRef.current.onSendMessage(msgObj);
    }

    function addChannelStream(channelID, outputStream) {
        if (recordingStreamNodeRef.current) {
            outputStream.connect(recordingStreamNodeRef.current);
        }
        if (channelID === 'XX') {
            deskInputNodeRef.current = outputStream;
            setHubRegistered(true);
        } else {
            outputStreamRef.current[channelID] = outputStream;
            outputStreamRef.current[channelID].connect(masterOutputGainRef.current);
        }
    }

    function channelStateChange(channelID, channelOn) {
        if (channelID !== 'XX') {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'channelStateChange';
            msgObj.callReceiver = sr.current.myRegisteredName;
            msgObj.callInitiator = inputChannelsRef.current[channelID].registeredName;
            msgObj.channelOn = channelOn;
            liveRef.current.onSendMessage(msgObj);
        }
    }

    function addStreamToDesk(channelID, stream) {
        if (typeof stream === 'undefined') {
            console.log('received stream on channel', channelID, 'but it is undefined');
            return;
        }
        localAudioRefs.current[channelID].srcObject = stream;
        localAudioRefs.current[channelID].muted = true;
        const streamSource = audioCTXRef.current.createMediaStreamSource(stream);
        const rawStream = stream.getVideoTracks().length > 0 ? stream : null;
        setInputChannels(prev => {
            const chs = [...prev];
            chs[channelID] = { ...chs[channelID], stream: streamSource, rawStream, remoteStreamReceived: true };
            return chs;
        });
    }

    function processWebRTCDebugEvent(channelID, debugObject) {
        const webRTCDebugResultObject = { registeredName: sr.current.myRegisteredName, debugObject };
        EventSystem.publish('webRTCdebug', webRTCDebugResultObject);
        const msgObj = getMessageObjectProForma();
        msgObj.sourceModuleName = 'remoteWebRTCdebug';
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.callInitiator = inputChannelsRef.current[channelID].registeredName;
        msgObj.client = 'AudioHub';
        msgObj.debugObject = debugObject;
        liveRef.current.onSendMessage(msgObj);
    }

    function gatherStats(channelD, reportObject) {
        setInputChannels(prev => {
            const chs = [...prev];
            chs[channelD] = { ...chs[channelD], reportObject };
            return chs;
        });
    }

    function sendConnectedTone(caller) {
        const channel = checkInputChannel(caller);
        if (channel === 99) { alert('Cannot send connected tone to ' + caller); return; }
        const gainNode = audioCTXRef.current.createGain();
        gainNode.gain.value = 0.005;
        const oscillator = audioCTXRef.current.createOscillator();
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(600, audioCTXRef.current.currentTime);
        oscillator.connect(gainNode);
        gainNode.connect(mediaTrackForPcRef.current[channel]);
        oscillator.start();
        timerIDRef.current = setTimeout(() => {
            oscillator.stop();
            oscillator.disconnect(gainNode);
            gainNode.disconnect(mediaTrackForPcRef.current[channel]);
        }, 80);
    }

    function showDebugMessage(messageObject) {
        if (messageObject.messageOriginator === messageObject.callInitiator) {
            console.log(messageObject.callInitiator, ' - ', messageObject.debug);
        }
    }

    function setRemoteGain(channel, level) {
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'setGain';
        msgObj.callInitiator = inputChannelsRef.current[channel].registeredName;
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.gain = level;
        liveRef.current.onSendMessage(msgObj);
    }

    function requestDebug(e) {
        const msgObj = getMessageObjectProForma();
        msgObj.callInitiator = inputChannelsRef.current[0].registeredName;
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.action = 'debugRequest';
        msgObj.command = e.target.name;
        msgObj.messageOriginator = sr.current.myRegisteredName;
        liveRef.current.onSendMessage(msgObj);
    }

    function handleChange(e) {
        if (e.target.name === 'unlockField') {
            if (e.target.value.length === 3) setShowModal(false);
        }
    }

    function updateAudioHubName(e) {
        if (e.target.value[0] === ' ') { setValidationError('error'); return; }
        const hubFullName = parameters.config.description + ' - ' + e.target.value;
        const isDup = registeredNames.findIndex(r => r.registeredName === hubFullName) !== -1;
        setValidationError(isDup ? 'error' : null);
        setAudioHubName(e.target.value);
    }

    function record(e) {
        if (e.target.name === 'record') {
            if (!mediaRecorderRef.current) return;
            if (recording) {
                mediaRecorderRef.current.stop();
            } else {
                chunksRef.current = [];
                recordingStartedAtRef.current = new Date();
                mediaRecorderRef.current.start(1000);
                setRecording(true);
            }
        }
    }

    function setupMixedAudioRecorder() {
        if (!window.MediaRecorder || !recordingStreamNodeRef.current) return false;

        const preferredTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/ogg'
        ];
        const mimeType = preferredTypes.find(type => MediaRecorder.isTypeSupported(type));
        const options = mimeType ? { mimeType } : undefined;

        try {
            mediaRecorderRef.current = new MediaRecorder(recordingStreamNodeRef.current.stream, options);
        } catch (err) {
            console.log('MediaRecorder unavailable for AudioHub mixed recording:', err);
            mediaRecorderRef.current = undefined;
            return false;
        }

        mediaRecorderRef.current.ondataavailable = e => {
            if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        mediaRecorderRef.current.onerror = e => {
            console.log('AudioHub mixed recording error:', e);
            setRecording(false);
        };
        mediaRecorderRef.current.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current.mimeType || 'audio/webm' });
            chunksRef.current = [];
            setRecording(false);
            if (blob.size === 0) return;

            if (audioURLRef.current) URL.revokeObjectURL(audioURLRef.current);
            audioURLRef.current = URL.createObjectURL(blob);
            const tempLink = document.createElement('a');
            const startedAt = recordingStartedAtRef.current || new Date();
            const timestamp = startedAt.toISOString().replace(/[:.]/g, '-');
            tempLink.href = audioURLRef.current;
            tempLink.setAttribute('download', `audiohub-mix-${timestamp}.webm`);
            tempLink.click();
        };
        return true;
    }

    function changeMasterGain(level) {
        const newGainValue = Math.pow(level / 33, 2);
        if (level < 5) level = 5;
        if (level > 100) level = 100;
        setOutputMasterGainVolume(level);
        if (demoMode) return;
        masterOutputGainRef.current.gain.value = newGainValue;
    }

    function selectVideoInput(eventKey) {
        setMyMediaDeviceClass(prev => {
            const mdc = prev;
            const sel = mdc.getSelectedDevices;
            sel.videoinput = eventKey === 'None' ? '' : eventKey;
            mdc.selectedDevices = sel;
            startPreview(mdc);
            return mdc;
        });
        // Re-acquire stream so new video device (or removal) takes effect immediately
        const hasActiveCalls = inputChannelsRef.current.some(ch => ch.callStatus !== 'none' && ch.callStatus !== 'bye');
        if (!hasActiveCalls) {
            stopLocalDevice();
            getLocalStream();
        }
    }

    function selectAudioInput(eventKey) {
        setMyMediaDeviceClass(prev => {
            const mdc = prev;
            const sel = mdc.getSelectedDevices;
            sel.audioinput = eventKey === 'None' ? '' : eventKey;
            mdc.selectedDevices = sel;
            return mdc;
        });
    }

    function selectAudioOutput(eventKey) {
        setMyMediaDeviceClass(prev => {
            const mdc = prev;
            const sel = mdc.getSelectedDevices;
            sel.audiooutput = eventKey;
            mdc.selectedDevices = sel;
            return mdc;
        });
    }

    function getDeviceMenuItem(kind, deviceInfo) {
        if (deviceInfo.kind === kind) {
            return <MenuItem key={deviceInfo.label} eventKey={deviceInfo.label}>{deviceInfo.label}</MenuItem>;
        }
        return null;
    }

    function createMenuItem(nameItem) {
        if (registeredNames.length > 0) {
            return <MenuItem key={nameItem.registeredName} eventKey={nameItem.registeredName} disabled={nameItem.callStatus === 'active'}>{nameItem.registeredName}</MenuItem>;
        }
        return null;
    }

    function stop() {
        mp3.current.pause();
    }

    function displayDebug() {
        EventSystem.publish('webRTCdebugState', true);
    }

    function toggleLocalCamera(muted) {
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !muted; });
        }
    }

    function stopPreview() {
        if (previewStreamRef.current) {
            previewStreamRef.current.getTracks().forEach(t => t.stop());
            previewStreamRef.current = null;
        }
        if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    }

    function startPreview(mdc) {
        stopPreview();
        const source = mdc || sr.current.myMediaDeviceClass;
        if (!source) return;
        const rawVideoConstraint = source.getDeviceID('videoinput');
        if (!rawVideoConstraint) return;
        const vid = rawVideoConstraint.deviceId?.exact;
        const videoConstraint = vid ? { deviceId: { exact: vid } } : true;
        navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: false })
            .then(stream => {
                previewStreamRef.current = stream;
                if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;
            })
            .catch(() => {});
    }

    // Camera preview in the device-select modal
    useEffect(() => {
        if (showModal) {
            startPreview();
        } else {
            stopPreview();
        }
    }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Render calculations ───────────────────────────────────────────────────
    let defaultAudioOutput = '';
    let defaultAudioInput = '';
    let defaultVideoInput = '';
    let localMediaDevices = [{ kind: '' }];

    if (myMediaDeviceClass !== null) {
        defaultAudioOutput = myMediaDeviceClass.getSelectedDevices.audiooutput;
        defaultAudioInput = myMediaDeviceClass.getSelectedDevices.audioinput === '' ? 'None' : myMediaDeviceClass.getSelectedDevices.audioinput;
        defaultVideoInput = myMediaDeviceClass.getSelectedDevices.videoinput === '' ? 'None' : myMediaDeviceClass.getSelectedDevices.videoinput;
        localMediaDevices = myMediaDeviceClass.getLocalMediaDevices;
    }

    let activeCalls = false;
    for (let x = 0; x < inputChannels.length; x++) {
        if (inputChannels[x].callStatus !== 'none') activeCalls = true;
    }
    const hubSwitchDisabledCalc = hubSwitchDisabled || !!validationError || activeCalls;

    const channelWidth = Math.round(containerWidth / 3);
    const channelBoxWidth = channelWidth - 16;
    const channelBoxHeight = 30 + Math.round(channelBoxWidth * 9 / 16);

    const sortedRaisedHands = [...raisedHands].sort((a, b) => a.raisedAt - b.raisedAt);
    const firstRaisedHand = sortedRaisedHands[0];
    const raisedHandOrder = sortedRaisedHands.reduce((acc, hand, index) => {
        acc[hand.registeredName] = index + 1;
        return acc;
    }, {});

    const upcoming = [];
    upcoming.push(
        <Col xs={2} key="upcoming" style={{ textAlign: 'center' }}>
            <span className="globalForeground" style={{ color: 'white', padding: 5 }}>Upcoming:</span>
        </Col>
    );
    for (let x = 0; x < registeredNames.length; x++) {
        let found = false;
        for (let i = 0; i < inputChannels.length; i++) {
            if (inputChannels[i].registeredName === registeredNames[x].registeredName ||
                registeredNames[x].sourceModuleName === 'AudioHub') {
                found = true;
            }
        }
        if (!found) {
            upcoming.push(
                <Col xs={2} key={registeredNames[x].registeredName} style={{ textAlign: 'center' }}>
                    <span style={{ color: 'white' }}>{registeredNames[x].registeredName}</span>
                </Col>
            );
        }
    }

    const arrayOfInboundCalls = [];
    for (let channel = 0; channel < inputChannels.length; channel++) {
        arrayOfInboundCalls.push(
            <div key={'Channel' + channel} style={{ width: channelBoxWidth, height: channelBoxHeight, margin: '5px', boxShadow: '#272727 2px 2px 2px 1px' }}>
                <Channel stream={inputChannels[channel].stream}
                    audioContext={audioCTXRef.current}
                    width={channelWidth}
                    height={channelBoxHeight}
                    name={demoMode ? (channel === 4 || channel === 5) ? '' : 'Caller ' + (channel + 1) : inputChannels[channel].registeredName}
                    mute={inputChannels[channel].remoteAudioMuted}
                    demoMode={demoMode}
                    reportObject={inputChannels[channel].reportObject}
                    channelID={channel}
                    label={channel + 1}
                    videoStream={inputChannels[channel].rawStream}
                    channelStateChange={channelStateChange}
                    hangupCall={hangupCall}
                    debug={requestDebug}
                    setRemoteGain={setRemoteGain}
                    sendKeepalive={sendKeepalive}
                    handRaisedOrder={raisedHandOrder[inputChannels[channel].registeredName] || 0}
                    lowerRaisedHand={lowerRaisedHand}
                    audioStreamAvailable={addChannelStream} />
            </div>
        );
    }

    let panelBodyHeight = 0;
    if (panelHeading.current !== null) panelBodyHeight = containerHeight - panelHeading.current.offsetHeight;

    let buttonHeight = 0, buttonRowHeight = 0;
    if (firstCol.current !== null) {
        buttonHeight = firstCol.current.offsetWidth * 0.6;
        buttonRowHeight = firstCol.current.offsetWidth * 0.7;
    }

    const audioHubNameStyle = {
        width: '100%', height: '30px', lineHeight: '30px',
        backgroundColor: parameters.colours.backgroundColor,
        color: parameters.colours.color, borderStyle: 'none', borderRadius: 0,
        borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66',
        padding: '0 6px',
        fontSize: '14px'
    };

    const rowStyle = { borderStyle: 'none', height: '100%', paddingTop: 0, paddingBottom: 0, backgroundColor: '#333333' };
    const bodyStyle = { paddingLeft: 0, paddingRight: 0, paddingBottom: 0, paddingTop: '0', border: '1px solid #484343', borderBottomRightRadius: '5px', borderBottomLeftRadius: '5px' };
    const cogGlyphStyle = { fontSize: '20px' };

    return (
        <div style={{ height: '100%', width: '100%' }} ref={container}>
            <Modal show={showModal} autoFocus>
                <Modal.Header><Modal.Title>Select Devices</Modal.Title></Modal.Header>
                <Modal.Body>
                    {/* Camera preview */}
                    {parameters.config.other_config.callType === 'video' && (
                        <Row style={{ marginBottom: 8 }}>
                            <Col xs={12} style={{ textAlign: 'center' }}>
                                <video ref={previewVideoRef} autoPlay muted playsInline
                                    style={{ width: '100%', maxHeight: '160px', objectFit: 'contain',
                                             borderRadius: 6, backgroundColor: '#111', display: 'block' }} />
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col xs={12} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px' }}>
                            <img src="staticImages/webcam-transparent.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2 }} />
                            <DropdownButton title={defaultVideoInput} bsStyle="primary"
                                id={'videoinput' + parameters.config.event_grouping_seq} name="videoinput" key="videoinput"
                                style={{ width: '100%', minHeight: '32px', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', fontSize: '13px' }}
                                onSelect={selectVideoInput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('videoinput', device))}
                            </DropdownButton>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px' }}>
                            <img src="staticImages/mic-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2, display: 'block' }} />
                            <DropdownButton title={defaultAudioInput} bsStyle="primary"
                                id={'connectWith' + parameters.config.event_grouping_seq} name="audioinput" key="audioinput"
                                style={{ width: '100%', minHeight: '32px', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', display: 'block', fontSize: '13px' }}
                                onSelect={selectAudioInput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('audioinput', device))}
                            </DropdownButton>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px' }}>
                            <img src="staticImages/speaker-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2, display: defaultAudioOutput.length === 0 ? 'none' : 'block' }} />
                            <DropdownButton title={defaultAudioOutput} bsStyle="primary"
                                id={'audiooutput' + parameters.config.event_grouping_seq} name="audiooutput" key="audiooutput"
                                style={{ width: '100%', minHeight: '32px', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', display: defaultAudioOutput.length === 0 ? 'none' : 'block', fontSize: '13px' }}
                                onSelect={selectAudioOutput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('audiooutput', device))}
                            </DropdownButton>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer><Button onClick={() => setShowModal(false)}>Close</Button></Modal.Footer>
            </Modal>

            <Tile bsStyle={parameters.config.colour} className="flex-col" style={rowStyle}>
                <Title ref={panelHeading} style={{ borderStyle: 'none', height: '38px', display: 'flex', alignItems: 'center', padding: '0 8px 0 30px' }}>
                    <span style={{ whiteSpace: 'nowrap', marginRight: '12px', fontSize: '14px' }}>{parameters.config.description}</span>
                    <form style={{ flex: 1, minWidth: 0, maxWidth: '220px', marginBottom: 0 }}>
                        <FormGroup controlId="userName" bsSize="small" validationState={validationError} style={{ width: '100%', marginBottom: 0 }}>
                            <input type="text" placeholder="Name" name="audioHubName" autoFocus={true}
                                value={audioHubName} disabled={hubRegistered}
                                onChange={updateAudioHubName}
                                style={audioHubNameStyle} />
                            <FormControl.Feedback />
                        </FormGroup>
                    </form>
                    <div onClick={displayDebug} style={{ position: 'absolute', right: 300, top: 0, cursor: 'pointer', padding: '5px' }}>
                        <i style={{ fontSize: '28px' }} className="material-icons">bug_report</i>
                    </div>
                    <div style={{ display: hubRegistered ? typeof mediaRecorderRef.current === 'undefined' ? 'none' : 'block' : 'none' }}>
                        <Button bsStyle="primary"
                            disabled={!recordingSupported}
                            style={{ height: '30px', overflow: 'hidden', textOverflow: 'ellipsis', position: 'absolute', top: '4px', width: '90px', display: 'block', right: '24px', padding: '5px', zIndex: 1 }}
                            name="record" onClick={record}
                            title={recordingSupported ? 'Record mixed AudioHub audio' : 'Recording is not supported in this browser'}>
                            {recording ? '  Stop' : 'Record'}
                        </Button>
                        <div className="recordingClass" style={{ display: recording ? 'block' : 'none', backgroundColor: parameters.config.colour }} />
                    </div>
                    <div style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', margin: 0, display: 'block', height: '8px', width: '8px', borderRadius: '50%', backgroundColor: hubRegistered ? typeof mediaRecorderRef.current === 'undefined' ? 'orange' : 'lime' : 'orange' }} />
                    <label htmlFor="icon-switch" style={{ position: 'absolute', right: 0, top: 0, margin: '5px', display: 'none' }}>
                        <Switch checked={hubRegistered} disabled={hubSwitchDisabledCalc} onChange={hubRegisterChanged}
                            offColor="#333333" height={28} width={56}
                            uncheckedIcon={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', fontSize: 11, color: 'white', paddingRight: 2 }}>Register</div>}
                            checkedIcon={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', fontSize: 15, color: 'white', paddingRight: 2 }}>On</div>}
                            className="react-switch" id="icon-switch" />
                    </label>
                </Title>

                <Body className="flex-grow globalBackground" style={bodyStyle}>
                    <Grid ref={panelBodyContainer} style={{ height: '100%', width: '100%', paddingLeft: 0, paddingRight: 0 }}>
                        <audio autoPlay ref={localAudioX} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio0} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio1} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio2} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio3} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio4} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                        <audio autoPlay ref={localAudio5} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />

                        <div ref={nonVideoRows}>
                            {firstRaisedHand && (
                                <div className="raisedHandHubAlert"
                                    onClick={() => lowerRaisedHand(firstRaisedHand.registeredName)}
                                    title="Lower raised hand">
                                    Hand raised first: {firstRaisedHand.registeredName}
                                </div>
                            )}
                            {upcoming}
                            <input type="text" style={{ height: 0, width: 0, position: 'absolute', zIndex: -2, left: '20px', top: '20px' }} name="dummy" />
                        </div>
                        <br />

                        <div style={{ display: 'flex', flexWrap: 'wrap', padding: '5px', marginTop: '5px', marginLeft: '5px' }}>
                            {arrayOfInboundCalls}
                            <div style={{ display: 'flex', flexDirection: 'column', margin: '5px' }}>
                                <div style={{ width: channelBoxWidth, height: channelBoxHeight, position: 'relative', boxShadow: '#272727 2px 2px 2px 1px' }}>
                                    <Channel stream={deskInputStreamPreMix}
                                        audioContext={audioCTXRef.current}
                                        width={channelWidth}
                                        height={channelBoxHeight}
                                        name={audioHubName}
                                        videoStream={localVideoStream}
                                        demoMode={demoMode}
                                        mute={false}
                                        reportObject={null}
                                        channelID="XX"
                                        sendKeepalive={sendKeepalive}
                                        channelStateChange={channelStateChange}
                                        audioStreamAvailable={addChannelStream}
                                        onVideoMuteChange={toggleLocalCamera} />
                                </div>
                                {/* Cog + master gain — anchored below the studio (XX) channel box */}
                                <div style={{ width: channelBoxWidth, display: 'flex', alignItems: 'center',
                                              padding: '4px 6px', backgroundColor: '#1a1a1a', borderRadius: '0 0 4px 4px' }}>
                                    <Glyphicon onClick={() => setShowModal(true)} className="cog"
                                        style={{ fontSize: '22px', cursor: 'pointer', color: '#ccc', marginRight: 10, flexShrink: 0, padding: '3px' }}
                                        glyph="cog" />
                                    <input type="range" min={0} max={100}
                                        value={outputMasterGainVolume}
                                        onChange={e => changeMasterGain(parseInt(e.target.value))}
                                        className="master-gain-slider"
                                        style={{ flex: 1, margin: 0 }} />
                                </div>
                            </div>
                        </div>

                        <Row style={{ marginLeft: 0, marginRight: 0, position: 'fixed', bottom: 0, left: 0, zIndex: 15, width: '100%', textAlign: 'center', display: terminationMessage.length > 0 ? 'block' : 'none' }}>
                            <span style={{ color: 'white', fontSize: '16pt', backgroundColor: 'gray', padding: '5px 10px', borderRadius: '20px' }}>{terminationMessage}</span>
                        </Row>

                        <div style={{ display: browserSupportFails ? 'block' : 'none', height: parameters.containerHeight.container + 'px', width: '100%', zIndex: 1000, backgroundColor: 'blue', color: 'yellow', fontSize: '26pt', position: 'fixed', top: 0, left: 0, padding: '10px' }}>
                            Your browser does not support webrtc audio and video, this module disabled
                        </div>
                    </Grid>
                </Body>
            </Tile>

            <audio src="audioClip.mp3" ref={mp3} onEnded={stop} type="audio/mpeg" />
        </div>
    );
}
